import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplyIcon from "@mui/icons-material/Reply";
import { Box } from "@mui/material";

import { InteractiveOptionView } from "@/helpers/chat-formatted-text";

type InteractiveOptionsProps = {
  options: InteractiveOptionView[];
  href?: string;
  isSentByUser?: boolean;
};

export default function InteractiveOptions({
  options,
  href,
  isSentByUser = false,
}: InteractiveOptionsProps) {
  if (options.length === 0) return null;

  const isList = !href && options.some((option) => option.subtitle);

  const surface = isSentByUser ? "rgba(255, 255, 255, 0.18)" : "rgba(11, 20, 26, 0.05)";
  const surfaceHover = isSentByUser ? "rgba(255, 255, 255, 0.28)" : "rgba(11, 20, 26, 0.09)";
  const actionColor = isSentByUser ? "#ffffff" : "#00a884";
  const subtitleColor = isSentByUser ? "rgba(255, 255, 255, 0.75)" : "rgba(11, 20, 26, 0.55)";

  return (
    <Box
      mt={1}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      {options.map((option, index) => {
        const isLink = Boolean(href) && index === 0;
        const Icon = isLink ? OpenInNewIcon : isList ? FormatListBulletedIcon : ReplyIcon;

        const sharedSx = {
          display: "flex",
          alignItems: "center",
          justifyContent: isList ? "flex-start" : "center",
          gap: 0.75,
          width: "100%",
          minHeight: 38,
          borderRadius: "0.5rem",
          backgroundColor: surface,
          color: actionColor,
          textAlign: isList ? "left" : "center",
          py: 0.75,
          px: 1.5,
          fontWeight: 500,
          fontSize: "0.875rem",
          lineHeight: 1.3,
          border: 0,
          cursor: isLink ? "pointer" : "default",
          textDecoration: "none",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: isLink ? surfaceHover : surface,
          },
          "& svg": {
            fontSize: "1rem",
            flexShrink: 0,
          },
        } as const;

        const content = (
          <>
            <Icon />
            <Box component="span" sx={{ overflowWrap: "anywhere", minWidth: 0 }}>
              <Box component="span" sx={{ display: "block" }}>
                {option.label}
              </Box>
              {option.subtitle && (
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    color: subtitleColor,
                    fontWeight: 400,
                    fontSize: "0.75rem",
                    mt: 0.25,
                  }}
                >
                  {option.subtitle}
                </Box>
              )}
            </Box>
          </>
        );

        if (isLink && href) {
          return (
            <Box
              key={`${option.label}-${index}`}
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              sx={sharedSx}
            >
              {content}
            </Box>
          );
        }

        return (
          <Box key={`${option.label}-${index}`} component="div" sx={sharedSx}>
            {content}
          </Box>
        );
      })}
    </Box>
  );
}
